import React, { useEffect, useMemo, useState } from "react";
import { AlertNotification, Loading, Text } from "@commonsku/styles";
import { toast } from "react-toastify";
import { getReportConfig, ReportConfig } from "./report/config";
import { EReportTypes } from "./report/routes/report_types";
import { ReportProvider } from "./report/report_context";
import StaticReportTable, {
  StaticReportRow,
} from "./report/table/StaticReportTable";
import { oauth } from "../utils";

interface PodInvoice {
  pod_invoice_id: string;
  date_invoiced: string | null;
  supplier_order_id: string | null;
  item_cost: number;
  pick_and_pack: number;
  shipping: number;
  total: number;
  invoice_spans_other_projects: boolean;
  order: { order_id: string; customer_po: string | null };
  client: { client_id: string | null; client_name: string | null };
  shop: { shop_id: string | null; shop_name: string | null };
  division: { division_id: string | null; division_name: string | null };
}

/**
 * The endpoint groups the commonsku records a line belongs to, but a column reads its fields
 * straight off the row and the sort comparators expect scalars, so they are spread out first.
 */
const toTableRow = ({
  order,
  client,
  shop,
  division,
  ...invoice
}: PodInvoice) => ({
  ...invoice,
  ...order,
  ...client,
  ...shop,
  ...division,
});

const PodBillsTable = ({ bills }: { bills: PodInvoice[] }) => {
  const [selectedRows, setSelectedRows] = useState<StaticReportRow[]>([]);
  const rows = useMemo(() => bills.map(toTableRow), [bills]);

  return (
    <StaticReportTable
      rows={rows}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
    />
  );
};

const ProjectPodBilling = ({ project }: { project: { job_id: string } }) => {
  const reportConfig: ReportConfig = useMemo(
    () => ({ ...getReportConfig(EReportTypes.PodProjectBill) }),
    [],
  );

  const [bills, setBills] = useState<PodInvoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    let current = true;

    setLoading(true);
    setFailed(false);
    oauth("GET", reportConfig.endpoint, { job_id: project.job_id })
      .then(({ json }) => {
        if (!current) {
          return;
        }
        setBills(Array.isArray(json) ? json : []);
        setLoading(false);
      })
      .catch(() => {
        if (!current) {
          return;
        }
        setFailed(true);
        setLoading(false);
        toast.error("Unable to load bills for this project");
      });

    return () => {
      current = false;
    };
  }, [reportConfig.endpoint, project.job_id]);

  return (
    <div className="main-content">
      {loading ? <Loading /> : null}
      {failed ? (
        <AlertNotification alertType="error">
          Bills could not be loaded. Please try again later.
        </AlertNotification>
      ) : null}
      {!loading && !failed ? (
        <ReportProvider reportConfig={reportConfig}>
          {bills.length > 0 ? (
            <PodBillsTable bills={bills} />
          ) : (
            <Text>No bills found for this project.</Text>
          )}
        </ReportProvider>
      ) : null}
    </div>
  );
};

export default ProjectPodBilling;
